import { useMemo } from 'react';

import { useSpecContractTerms } from '@/common/hooks/contract';
import { type ContractStepTermsProps } from '@/common/types/contract';
import {
  selectActiveBlitzContractStep,
  selectSpecContractIsFail,
} from '@/redux/selectors/contractSelectors';
import { useAppSelector } from '@/redux/store';

interface useBlitzContractTermsReturnType {
  list: ContractStepTermsProps[];
  activeStep?: number;
  isFail: boolean;
}

const useBlitzContractTerms = (
  kind: 'Blitz' | 'BlitzPlus',
): useBlitzContractTermsReturnType => {
  const activeStepProps = useSpecContractTerms(kind);
  const activeStep = useAppSelector((state) =>
    selectActiveBlitzContractStep(state, kind),
  );
  const isFail = useAppSelector((state) =>
    selectSpecContractIsFail(state, kind),
  );
  const list = useMemo<ContractStepTermsProps[]>(() => {
    if (!activeStep || !activeStepProps) return [];
    const stepsProps = [{ step: 1 }, { step: 2 }, { step: 3 }, { step: 4 }];
    const completeStepProps: Omit<ContractStepTermsProps, 'step'> = {
      kind: 'Blitz',
      hasContractComplete: true,
      hasContractFail: false,
    };

    const nextStepProps: Omit<ContractStepTermsProps, 'step'> = {
      kind: 'Blitz',
      hasContractComplete: false,
      hasContractFail: false,
    };

    return stepsProps.map(({ step }) => {
      if (step < activeStep) {
        return { ...completeStepProps, step };
      }

      if (step > activeStep) {
        return { ...nextStepProps, step };
      }

      return { ...activeStepProps, kind: 'Blitz', step };
    });
  }, [activeStep, activeStepProps]);

  return { list, activeStep, isFail };
};

export { useBlitzContractTerms };
